import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  IconButton,
  InputAdornment,
  TablePagination,
  Typography,
} from '@mui/material';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { ArrowSquareDownIcon, SearchIcon } from 'assets/icons';
import { PrimaryLightBgCard, PrimaryTextField } from 'components';
import { dateTimeFormat } from 'utils/formats';
import { mediaModalActions } from 'base/common/media-modal/slice';
import { selectMediaResult } from 'base/common/media-modal/selector';
import { IMediaItemData } from 'base/common/media-modal/interface';
import { AccordionStyle, ThumbnailImgStyle } from '../../styles';

const ExpertMedia = ({ onMediaSelect } : { onMediaSelect: (media: IMediaItemData) => void; }) => {
  const dispatch = useDispatch();
  const [keyword, setKeyword] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const { result, total } = useSelector(selectMediaResult);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  const expertMedias: IMediaItemData[] = React.useMemo(
    () => result.filter(
      (media: IMediaItemData) => media.fileCategory !== 'document',
    ),
    [result],
  );

  React.useEffect(() => {
    dispatch(mediaModalActions.fetchMedia({
      keyword,
      page: (page + 1),
      limit: rowsPerPage,
      type: 'image,video',
    }));
  }, [dispatch, page, keyword, rowsPerPage]);

  const handleMediaSelect = (media: IMediaItemData) => {
    onMediaSelect(media);
  };

  return (
    <Accordion sx={[AccordionStyle, { mt: 2.5 }]} defaultExpanded disableGutters>
      <AccordionSummary
        expandIcon={<ArrowSquareDownIcon sx={{ color: 'primary.dark', fontSize: 16 }} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Typography variant="body2" sx={{}}>
          Your media
        </Typography>
      </AccordionSummary>

      <AccordionDetails>

        <PrimaryTextField
          placeholder="Search media"
          sx={{ maxHeight: 38, mb: 2 }}
          endAdornment={(
            <InputAdornment position="end">
              <SearchIcon sx={{ fontSize: 16 }} />
            </InputAdornment>
          )}
          onChange={handleChange}
        />
        {expertMedias && expertMedias.length > 0 ? expertMedias.map((media) => (
          <IconButton
            key={media.id}
            disableRipple
            sx={{ p: 0, display: 'flex', width: '100%' }}
            onClick={() => { handleMediaSelect(media); }}
          >
            <PrimaryLightBgCard className="flex-grow-1 d-flex flex-nowrap overflow-hidden" sx={{ p: 1.5, mb: 1 }}>
              <Avatar
                variant="rounded"
                alt="The image"
                src={media.thumbnail}
                sx={ThumbnailImgStyle}
              >
                <PermMediaIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <div className="text-start">
                <Typography variant="body2" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                  {media.title}
                </Typography>
                {media.createdAt && media.createdAt.length ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                    {dateTimeFormat(media.createdAt)}
                  </Typography>
                ) : null}
              </div>
            </PrimaryLightBgCard>
          </IconButton>
        )) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            { keyword && keyword.length > 0 ? 'Media not found from this keyword' : 'You have no media uploaded yet' }
          </Typography>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default ExpertMedia;
